<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { t } from '../i18nStore';

  let items = [];
  let isLoading = true;
  let error = '';
  let successMsg = '';

  // Form State
  let showModal = false;
  let isEditing = false;
  let formId = '';
  
  // Multilingual name & description maps
  let formNameEn = '';
  let formNameZh = '';
  let formNameJa = '';
  
  let formDescEn = '';
  let formDescZh = '';
  let formDescJa = '';
  
  let formPointsRequired = 100;
  let formCountry = 'HK';
  let formQuantity = 10;
  
  // Date inputs
  let formStartDate = '';
  let formEndDate = '';
  let formVouchers = [];

  $: formQuantity = formVouchers.filter(v => !v.isRedeemed).length;

  function addVoucherRow() {
    const defaultExpiry = new Date();
    defaultExpiry.setMonth(defaultExpiry.getMonth() + 3);
    formVouchers = [...formVouchers, {
      code: '',
      expiryDate: defaultExpiry.toISOString().slice(0, 16),
      isRedeemed: false
    }];
  }

  function removeVoucherRow(index) {
    formVouchers = formVouchers.filter((_, i) => i !== index);
  }

  onMount(async () => {
    await fetchItems();
  });

  async function fetchItems() {
    isLoading = true;
    error = '';
    try {
      const res = await authFetch(getApiUrl('/admin/campaign/rewards'));
      if (res.ok) {
        items = await res.json();
      } else {
        const data = await res.json();
        error = data.error || 'Failed to load items';
      }
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function openCreateModal() {
    isEditing = false;
    formId = '';
    formNameEn = '';
    formNameZh = '';
    formNameJa = '';
    formDescEn = '';
    formDescZh = '';
    formDescJa = '';
    formPointsRequired = 100;
    formCountry = 'HK';
    formVouchers = [];
    
    // Set default dates: start now, end 1 year from now
    const now = new Date();
    const oneYearLater = new Date();
    oneYearLater.setFullYear(now.getFullYear() + 1);
    
    formStartDate = now.toISOString().slice(0, 16); // format: YYYY-MM-DDTHH:MM
    formEndDate = oneYearLater.toISOString().slice(0, 16);
    
    showModal = true;
    successMsg = '';
    error = '';
  }

  function openEditModal(item) {
    isEditing = true;
    formId = item.id;
    formNameEn = item.name?.en || '';
    formNameZh = item.name?.['zh-HK'] || '';
    formNameJa = item.name?.ja || '';
    formDescEn = item.description?.en || '';
    formDescZh = item.description?.['zh-HK'] || '';
    formDescJa = item.description?.ja || '';
    formPointsRequired = item.pointsRequired || 0;
    formCountry = item.country || 'HK';
    
    formVouchers = (item.vouchers || []).map(v => ({
      code: v.code,
      expiryDate: v.expiryDate ? new Date(v.expiryDate).toISOString().slice(0, 16) : '',
      isRedeemed: !!v.isRedeemed
    }));
    
    if (item.startDate) {
      formStartDate = new Date(item.startDate).toISOString().slice(0, 16);
    } else {
      formStartDate = '';
    }
    
    if (item.endDate) {
      formEndDate = new Date(item.endDate).toISOString().slice(0, 16);
    } else {
      formEndDate = '';
    }
    
    showModal = true;
    successMsg = '';
    error = '';
  }

  async function saveItem() {
    error = '';
    successMsg = '';
    
    if (!formNameEn) {
      error = 'English Name is required.';
      return;
    }
    if (!formStartDate || !formEndDate) {
      error = 'Start and End dates are required.';
      return;
    }
    if (new Date(formStartDate) >= new Date(formEndDate)) {
      error = 'Start date must be before end date.';
      return;
    }

    const payload = {
      id: formId,
      name: {
        'en': formNameEn,
        'zh-HK': formNameZh,
        'ja': formNameJa
      },
      description: {
        'en': formDescEn,
        'zh-HK': formDescZh,
        'ja': formDescJa
      },
      pointsRequired: Number(formPointsRequired),
      country: formCountry,
      quantity: Number(formQuantity),
      startDate: new Date(formStartDate).toISOString(),
      endDate: new Date(formEndDate).toISOString(),
      vouchers: formVouchers.map(v => ({
        code: v.code,
        expiryDate: new Date(v.expiryDate).toISOString(),
        isRedeemed: !!v.isRedeemed
      }))
    };

    try {
      const url = isEditing 
        ? getApiUrl(`/admin/campaign/rewards/${formId}`) 
        : getApiUrl('/admin/campaign/rewards');
      
      const method = isEditing ? 'PUT' : 'POST';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        successMsg = isEditing ? 'Reward updated successfully!' : 'Reward created successfully!';
        showModal = false;
        await fetchItems();
      } else {
        const data = await res.json();
        error = data.error || 'Failed to save reward item.';
      }
    } catch (e) {
      error = e.message;
    }
  }

  async function deleteItem(id) {
    if (!confirm('Are you sure you want to delete this reward item?')) return;
    try {
      const res = await authFetch(getApiUrl(`/admin/campaign/rewards/${id}`), {
        method: 'DELETE'
      });
      if (res.ok) {
        successMsg = 'Reward item deleted successfully!';
        await fetchItems();
      } else {
        const data = await res.json();
        error = data.error || 'Failed to delete reward item.';
      }
    } catch (e) {
      error = e.message;
    }
  }

  function getLocalizedText(field, lang) {
    if (!field) return '';
    return field[lang] || field['en'] || '';
  }
</script>

<div class="admin-rewards-container">
  <div class="glass-header">
    <div class="header-main">
      <h2>Redeemable Rewards Catalogue Manager</h2>
      <button class="add-btn" on:click={openCreateModal}>+ Create New Reward</button>
    </div>
    <p class="subtitle">Add, edit, or remove country-specific items that users can redeem using Snapper Points.</p>
  </div>

  {#if successMsg}
    <div class="alert success">{successMsg}</div>
  {/if}

  {#if error}
    <div class="alert danger">{error}</div>
  {/if}

  {#if isLoading}
    <div class="spinner-container"><div class="spinner"></div></div>
  {:else if items.length === 0}
    <div class="empty-state">
      <p>No rewards configured in the database.</p>
      <button class="action-btn" on:click={openCreateModal}>Configure First Reward</button>
    </div>
  {:else}
    <div class="table-card">
      <div class="table-wrapper">
        <table class="premium-table">
          <thead>
            <tr>
              <th>Country</th>
              <th>Reward Details (EN)</th>
              <th>Points Cost</th>
              <th>Qty Limit</th>
              <th>Effective period</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {#each items as item}
              <tr>
                <td><span class="country-badge">{item.country}</span></td>
                <td>
                  <div class="item-name">{getLocalizedText(item.name, 'en')}</div>
                  <div class="item-desc">{getLocalizedText(item.description, 'en')}</div>
                  <div class="translations-indicator">
                    {#if item.name?.['zh-HK']}<span class="lang-tag">ZH-HK</span>{/if}
                    {#if item.name?.ja}<span class="lang-tag">JA</span>{/if}
                  </div>
                </td>
                <td class="points-val">{item.pointsRequired.toLocaleString()} pts</td>
                <td>
                  <span class="qty-badge {item.quantity === 0 ? 'out' : ''}">
                    {item.quantity} units
                  </span>
                </td>
                <td>
                  <div class="date-text">Start: {new Date(item.startDate).toLocaleDateString()}</div>
                  <div class="date-text text-muted">End: {new Date(item.endDate).toLocaleDateString()}</div>
                </td>
                <td>
                  {#if new Date() < new Date(item.startDate)}
                    <span class="status-badge upcoming">Upcoming</span>
                  {:else if new Date() > new Date(item.endDate)}
                    <span class="status-badge expired">Expired</span>
                  {:else if item.quantity === 0}
                    <span class="status-badge out">Out of Stock</span>
                  {:else}
                    <span class="status-badge active">Active</span>
                  {/if}
                </td>
                <td>
                  <div class="action-group">
                    <button class="edit-btn" on:click={() => openEditModal(item)}>Edit</button>
                    <button class="delete-btn" on:click={() => deleteItem(item.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  {/if}
</div>

<!-- Modal Dialog -->
{#if showModal}
  <div class="modal-backdrop" on:click={() => showModal = false}>
    <div class="modal-content" on:click|stopPropagation>
      <div class="modal-header">
        <h3>{isEditing ? 'Edit Redeemable Reward' : 'Create Redeemable Reward'}</h3>
        <button class="close-btn-x" on:click={() => showModal = false}>&times;</button>
      </div>
      <div class="modal-body">
        <form on:submit|preventDefault={saveItem} class="premium-form">
          
          {#if !isEditing}
            <div class="form-group">
              <label for="rewardId">Unique ID (Optional - will be auto-generated if left blank)</label>
              <input type="text" id="rewardId" bind:value={formId} placeholder="e.g. voucher_hk_50" />
            </div>
          {/if}

          <!-- Multilingual Names -->
          <div class="multilingual-section">
            <span class="section-title">Item Name</span>
            <div class="form-group inline-group">
              <label for="nameEn">English <span class="required">*</span></label>
              <input type="text" id="nameEn" bind:value={formNameEn} placeholder="e.g. HK$50 Supermarket Voucher" required />
            </div>
            <div class="form-group inline-group">
              <label for="nameZh">Chinese (HK)</label>
              <input type="text" id="nameZh" bind:value={formNameZh} placeholder="例如：HK$50超市禮券" />
            </div>
            <div class="form-group inline-group">
              <label for="nameJa">Japanese</label>
              <input type="text" id="nameJa" bind:value={formNameJa} placeholder="例：HK$50スーパーマーケットクーポン" />
            </div>
          </div>

          <!-- Multilingual Descriptions -->
          <div class="multilingual-section">
            <span class="section-title">Item Description</span>
            <div class="form-group inline-group">
              <label for="descEn">English</label>
              <textarea id="descEn" bind:value={formDescEn} placeholder="e.g. Valid at all outlets..." rows="2"></textarea>
            </div>
            <div class="form-group inline-group">
              <label for="descZh">Chinese (HK)</label>
              <textarea id="descZh" bind:value={formDescZh} placeholder="例如：適用於全港分店..." rows="2"></textarea>
            </div>
            <div class="form-group inline-group">
              <label for="descJa">Japanese</label>
              <textarea id="descJa" bind:value={formDescJa} placeholder="例：全店舗で利用可能..." rows="2"></textarea>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="pointsReq">Points Required</label>
              <input type="number" id="pointsReq" bind:value={formPointsRequired} min="0" required />
            </div>
            <div class="form-group">
              <label for="countryCode">Target Country</label>
              <select id="countryCode" bind:value={formCountry}>
                <option value="HK">Hong Kong (HK)</option>
                <option value="GB">United Kingdom (GB)</option>
                <option value="JP">Japan (JP)</option>
              </select>
            </div>
            <div class="form-group">
              <label for="qtyLimit">Stock Quantity (Auto-calculated)</label>
              <input type="number" id="qtyLimit" bind:value={formQuantity} min="0" readonly style="background-color: #f1f5f9; cursor: not-allowed;" />
            </div>
          </div>

          <!-- Vouchers Management Section -->
          <div class="multilingual-section">
            <span class="section-title">Pre-defined Vouchers Ledger</span>
            <p class="subtitle" style="margin-bottom: 1rem; font-size: 0.85rem; color: #64748b;">
              Add voucher codes and their expiry dates. Stock quantity is automatically computed based on the number of unredeemed vouchers.
            </p>
            {#if formVouchers.length === 0}
              <p style="color: #64748b; font-style: italic; font-size: 0.9rem; margin-bottom: 1rem;">No voucher codes added yet.</p>
            {:else}
              <div style="display: flex; flex-direction: column; gap: 0.75rem; margin-bottom: 1rem;">
                {#each formVouchers as voucher, i}
                  <div class="form-row" style="align-items: center; gap: 0.5rem; margin-bottom: 0;">
                    <div class="form-group" style="flex: 2; margin-bottom: 0;">
                      <label for="v-code-{i}" style="font-size: 0.75rem; color: #64748b;">Code</label>
                      <input type="text" id="v-code-{i}" placeholder="e.g. SP-ABCD-1234" bind:value={voucher.code} disabled={voucher.isRedeemed} required style="margin-top: 0.25rem;" />
                    </div>
                    <div class="form-group" style="flex: 2; margin-bottom: 0;">
                      <label for="v-date-{i}" style="font-size: 0.75rem; color: #64748b;">Expiry Date</label>
                      <input type="datetime-local" id="v-date-{i}" bind:value={voucher.expiryDate} disabled={voucher.isRedeemed} required style="margin-top: 0.25rem;" />
                    </div>
                    <div class="form-group" style="flex: 1.2; margin-bottom: 0; text-align: center;">
                      <label style="font-size: 0.75rem; color: #64748b; display: block; margin-bottom: 0.25rem;">Status</label>
                      <span class="status-badge {voucher.isRedeemed ? 'expired' : 'active'}" style="display: inline-block; padding: 0.4rem 0.6rem; font-size: 0.85rem; border-radius: 6px;">
                        {voucher.isRedeemed ? 'Redeemed' : 'Active'}
                      </span>
                    </div>
                    <div style="flex: 0.5; display: flex; align-items: flex-end; height: 100%;">
                      <button type="button" class="delete-btn" style="padding: 0.5rem; margin: 1.25rem 0 0 0; width: 100%; border-radius: 6px;" on:click={() => removeVoucherRow(i)}>
                        Remove
                      </button>
                    </div>
                  </div>
                {/each}
              </div>
            {/if}
            <button type="button" class="add-btn" style="font-size: 0.85rem; padding: 0.5rem 1rem; background: #6366f1;" on:click={addVoucherRow}>
              + Add Voucher Code
            </button>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="startDate">Effective Period Start</label>
              <input type="datetime-local" id="startDate" bind:value={formStartDate} required />
            </div>
            <div class="form-group">
              <label for="endDate">Effective Period End</label>
              <input type="datetime-local" id="endDate" bind:value={formEndDate} required />
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn-cancel" on:click={() => showModal = false}>Cancel</button>
            <button type="submit" class="btn-save">Save Reward Item</button>
          </div>
        </form>
      </div>
    </div>
  </div>
{/if}

<style>
  .admin-rewards-container {
    max-width: 1100px;
    margin: 2rem auto;
    padding: 0 1.5rem;
    text-align: left;
  }

  .glass-header {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(226, 232, 240, 0.8);
    border-radius: 16px;
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.03);
    border-left: 5px solid #6366f1;
  }

  .header-main {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .glass-header h2 {
    margin: 0;
    font-size: 1.8rem;
    color: #1e293b;
    font-weight: 800;
  }

  .subtitle {
    margin: 0.5rem 0 0 0;
    color: #64748b;
    font-size: 0.95rem;
  }

  .add-btn {
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: white;
    font-weight: 600;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }

  .add-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
  }

  .alert {
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1.5rem;
    font-weight: 500;
  }

  .alert.success {
    background: #ecfdf5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  }

  .alert.danger {
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fca5a5;
  }

  /* Table styling */
  .table-card {
    background: white;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    overflow: hidden;
  }

  .table-wrapper {
    overflow-x: auto;
  }

  .premium-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.95rem;
  }

  .premium-table th {
    background-color: #f8fafc;
    color: #475569;
    font-weight: 700;
    text-align: left;
    padding: 1rem 1.5rem;
    border-bottom: 2px solid #edf2f7;
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.05em;
  }

  .premium-table td {
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: middle;
  }

  .country-badge {
    background: #e0e7ff;
    color: #3730a3;
    font-weight: 700;
    padding: 0.25rem 0.6rem;
    border-radius: 6px;
    font-size: 0.85rem;
  }

  .item-name {
    font-weight: 600;
    color: #1e293b;
    font-size: 1rem;
  }

  .item-desc {
    font-size: 0.85rem;
    color: #64748b;
    margin-top: 0.25rem;
  }

  .translations-indicator {
    display: flex;
    gap: 0.3rem;
    margin-top: 0.4rem;
  }

  .lang-tag {
    background: #f1f5f9;
    color: #475569;
    font-size: 0.7rem;
    font-weight: 700;
    padding: 0.1rem 0.35rem;
    border-radius: 4px;
  }

  .points-val {
    font-weight: 700;
    color: #4f46e5;
  }

  .qty-badge {
    background: #f0fdf4;
    color: #166534;
    font-weight: 600;
    padding: 0.25rem 0.6rem;
    border-radius: 6px;
    font-size: 0.85rem;
  }

  .qty-badge.out {
    background: #fef2f2;
    color: #991b1b;
  }

  .date-text {
    font-size: 0.8rem;
    color: #334155;
  }

  .text-muted {
    color: #94a3b8;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.8rem;
    font-weight: 600;
  }

  .status-badge.active {
    background-color: #d1fae5;
    color: #065f46;
  }

  .status-badge.upcoming {
    background-color: #e0f2fe;
    color: #0369a1;
  }

  .status-badge.expired {
    background-color: #f1f5f9;
    color: #475569;
  }

  .status-badge.out {
    background-color: #fee2e2;
    color: #991b1b;
  }

  .action-group {
    display: flex;
    gap: 0.5rem;
  }

  .edit-btn, .delete-btn {
    border: 1px solid #cbd5e1;
    background: white;
    padding: 0.4rem 0.8rem;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .edit-btn {
    color: #2563eb;
    border-color: #bfdbfe;
  }

  .edit-btn:hover {
    background: #eff6ff;
  }

  .delete-btn {
    color: #dc2626;
    border-color: #fca5a5;
  }

  .delete-btn:hover {
    background: #fef2f2;
  }

  /* Modals */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(15, 23, 42, 0.4);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-content {
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 650px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  .modal-header {
    padding: 1.5rem;
    border-bottom: 1px solid #f1f5f9;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
    color: #1e293b;
    font-weight: 700;
  }

  .close-btn-x {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #94a3b8;
    cursor: pointer;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .premium-form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .form-group label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #475569;
  }

  .form-group input, .form-group textarea, .form-group select {
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 0.6rem 0.8rem;
    font-size: 0.95rem;
    background: #f8fafc;
    transition: all 0.2s;
  }

  .form-group input:focus, .form-group textarea:focus, .form-group select:focus {
    border-color: #6366f1;
    background: white;
    outline: none;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  }

  .required {
    color: #ef4444;
  }

  .multilingual-section {
    border: 1px solid #f1f5f9;
    background: #f8fafc;
    border-radius: 10px;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }

  .multilingual-section .section-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #334155;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 0.3rem;
    margin-bottom: 0.2rem;
  }

  .form-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1rem;
    border-top: 1px solid #f1f5f9;
    padding-top: 1.25rem;
  }

  .btn-cancel {
    background: #f1f5f9;
    color: #475569;
    border: none;
    padding: 0.6rem 1.2rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
  }

  .btn-save {
    background: #6366f1;
    color: white;
    border: none;
    padding: 0.6rem 1.2rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
  }

  .spinner-container {
    display: flex;
    justify-content: center;
    padding: 4rem;
  }

  .spinner {
    border: 4px solid rgba(0, 0, 0, 0.1);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border-left-color: #6366f1;
    animation: spin 1s linear infinite;
  }

  .empty-state {
    text-align: center;
    padding: 4rem 2rem;
    background: white;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
  }

  .empty-state p {
    color: #64748b;
    margin-bottom: 1.5rem;
  }

  .action-btn {
    background: #6366f1;
    color: white;
    border: none;
    padding: 0.6rem 1.2rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
</style>
